import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FloppyDisk, X, CaretDown, CheckCircle, UsersThree } from '@phosphor-icons/react';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../config';

const COMPANY_OPTIONS = [
  'THERMO-SOLUTIONS MONTEURS',
  'DUURZAAM ADVIES LOKET',
  'JANSEN EN DE JONG',
  'OUDE WOLDERS',
  'VMK',
];

/** API-only fields that must not be sent back from merged edit payloads. */
const READONLY_ORDER_KEYS = [
  'id',
  'status',
  'created_at',
  'created_by',
  'scheduled_date',
  'mobile_work_status',
  'pending_closed_release',
  'worker_notes_json',
  'client_signature_json',
  'mobile_photos_json',
  'planning_marker_color',
];

const BESTELLING_ITEMS = [
  { key: 'kozijnen', labelKey: 'kozijnen' },
  { key: 'glas', labelKey: 'glas' },
  { key: 'deurvulling', labelKey: 'deurvulling' },
  { key: 'rooster', labelKey: 'rooster' },
  { key: 'horren', labelKey: 'horren' },
  { key: 'keralit', labelKey: 'keralit' },
  { key: 'binnenafwerking', labelKey: 'binnenafwerking' },
  { key: 'vensterbanken', labelKey: 'vensterbanken' },
  { key: 'dakramen_bmn', labelKey: 'dakramenBmn' },
  { key: 'plastisol', labelKey: 'plastisol' },
  { key: 'raamdorpelstenen', labelKey: 'raamdorpelstenen' },
  { key: 'stelpakket', labelKey: 'stelpakket' },
  { key: 'rolluiken_screens', labelKey: 'rolluikenScreens' },
  { key: 'sandwich', labelKey: 'sandwich' },
  { key: 'container', labelKey: 'container' },
  { key: 'diversen', labelKey: 'diversen' },
];

const WerkvoorbereidingForm = ({
  offer,
  onClose,
  onSuccess,
  initialData = null,
  mode = 'create',
  orderId = null,
}) => {
  const { t } = useLanguage();
  const [showCustomCompany, setShowCustomCompany] = useState(() => {
    if (!initialData?.company_name) return false;
    const upper = String(initialData.company_name).toUpperCase();
    return !COMPANY_OPTIONS.includes(upper);
  });
  const isEditMode = mode === 'edit';
  const isManualOffer = Boolean(offer?.manual);

  const buildInitialFormData = () => {
    const manualOfferId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? `manual:${crypto.randomUUID()}`
        : `manual:${Date.now()}`;
    const data = {
      offer_id: isManualOffer ? manualOfferId : offer?.id || '',
      manual_reference: '',
      offer_total_amount:
        offer?.total_amount != null && offer?.total_amount !== ''
          ? Number(offer.total_amount)
          : null,
      company_name: COMPANY_OPTIONS[0],
      monteurs: '',
      client_name: offer?.contact?.name || '',
      address: offer?.contact?.address || '',
      postcode: offer?.contact?.postcode || '',
      phone: offer?.contact?.phone || '',
      email: offer?.contact?.email || '',
      kozijn_buiten: '9001',
      draaideel_buiten: '9001',
      kozijn_binnen: '9001',
      folie_kozijn_buiten: true,
      folie_draaideel_buiten: true,
      folie_kozijn_binnen: false,
      verkoopdatum: '',
      inmeetdatum: '',
      montagedatum: '',
      montagedagen: 0,
      glasbok_required: false,
      notes: '',
    };
    BESTELLING_ITEMS.forEach(({ key }) => {
      data[`${key}_besteldatum`] = '';
      data[`${key}_leverdatum`] = '';
    });
    if (initialData && typeof initialData === 'object') {
      const merged = { ...data, ...initialData };
      if (merged.company_name) {
        merged.company_name = String(merged.company_name).toUpperCase();
      }
      return merged;
    }
    return data;
  };

  const [formData, setFormData] = useState(buildInitialFormData);
  const [teams, setTeams] = useState([]);
  const [monteursOpen, setMonteursOpen] = useState(false);
  const [monteurSearch, setMonteurSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const clientFieldsEditable = useMemo(() => {
    return (
      isManualOffer ||
      String(formData.company_name || '').trim().toUpperCase() === 'DUURZAAM ADVIES LOKET'
    );
  }, [formData.company_name, isManualOffer]);

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, {
          sensitivity: 'base',
        }),
      ),
    [teams],
  );

  const assignedTeam = useMemo(() => {
    const m = String(formData.monteurs || '').trim();
    if (!m) return null;
    return (
      teams.find(
        (tm) => String(tm.name || '').trim().toLowerCase() === m.toLowerCase(),
      ) || null
    );
  }, [formData.monteurs, teams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/teams`, { withCredentials: true });
        if (!cancelled) setTeams(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setTeams([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        company_name: (formData.company_name || '').toUpperCase(),
        diversen_leverdatum: '',
      };
      READONLY_ORDER_KEYS.forEach((k) => {
        delete payload[k];
      });
      if (isManualOffer) {
        const ref = String(formData.manual_reference || '').trim();
        if (ref) {
          payload.offer_id = `manual:${ref}`;
        }
        delete payload.manual_reference;
      }
      const amountRaw = payload.offer_total_amount;
      if (amountRaw === '' || amountRaw == null) {
        payload.offer_total_amount = null;
      } else {
        const parsed = Number(amountRaw);
        payload.offer_total_amount = Number.isFinite(parsed) ? parsed : null;
      }
      const m = String(formData.monteurs || '').trim();
      payload.monteurs = m;
      const teamMatch = teams.find(
        (tm) => String(tm.name || '').trim().toLowerCase() === m.toLowerCase(),
      );
      payload.assigned_team_id = teamMatch ? String(teamMatch.id) : null;
      if (isEditMode && orderId) {
        await axios.put(`${API}/orders/${orderId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/orders`, payload, { withCredentials: true });
      }
      onSuccess();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const fallback = isEditMode ? t('orderUpdateFailed') : t('orderCreateFailed');
      toast.error(typeof detail === 'string' ? detail : fallback);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="werkvoorbereiding-form">
      {/* Company Selection */}
      <div className="form-section">
        <h3 className="form-section-title">{t('companySection')}</h3>
        <div className="form-grid">
          <div className="form-group col-span-2">
            <label className="form-label">{t('companyName')}</label>
            <select
              value={showCustomCompany ? '__custom__' : formData.company_name}
              onChange={(e) => {
                if (e.target.value === '__custom__') {
                  setShowCustomCompany(true);
                  setFormData(prev => ({ ...prev, company_name: '' }));
                } else {
                  setShowCustomCompany(false);
                  setFormData(prev => ({ ...prev, company_name: String(e.target.value).toUpperCase() }));
                }
              }}
              className="vmk-input uppercase tracking-wide"
              data-testid="form-company-select"
            >
              {COMPANY_OPTIONS.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="__custom__">{t('customCompanyOption')}</option>
            </select>
            {showCustomCompany && (
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, company_name: e.target.value.toUpperCase() }))
                }
                className="vmk-input mt-3 uppercase tracking-wide"
                placeholder={t('customCompanyPlaceholder')}
                autoFocus
                data-testid="form-company-custom"
              />
            )}
          </div>
          <div className="form-group col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-2 mb-1.5">
              <label className="form-label mb-0">{t('monteurs')}</label>
              {teams.length > 0 && (
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                  {teams.length} {t('teamsAvailable')}
                </span>
              )}
            </div>

            <div className="space-y-2">
              <Popover
                open={teams.length > 0 ? monteursOpen : false}
                onOpenChange={(open) => {
                  if (!teams.length) return;
                  setMonteursOpen(open);
                  if (open) setMonteurSearch(formData.monteurs || '');
                }}
              >
                <PopoverTrigger asChild>
                  <div className="relative">
                    <input
                      type="text"
                      name="monteurs"
                      value={formData.monteurs}
                      onFocus={() => {
                        if (!teams.length) return;
                        setMonteursOpen(true);
                        setMonteurSearch(formData.monteurs || '');
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        if (!teams.length) return;
                        setMonteurSearch(e.target.value || '');
                        if (!monteursOpen) setMonteursOpen(true);
                      }}
                      className="vmk-input pr-11"
                      placeholder={t('monteursPlaceholder')}
                      autoComplete="off"
                      data-testid="form-monteurs"
                    />
                    {teams.length > 0 && (
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                        <CaretDown
                          className={cn(
                            'h-4 w-4 text-[#64748b] transition-transform',
                            monteursOpen && 'rotate-180',
                          )}
                          weight="bold"
                          aria-hidden
                        />
                      </span>
                    )}
                  </div>
                </PopoverTrigger>
                {teams.length > 0 && (
                  <PopoverContent
                    align="start"
                    sideOffset={8}
                    className="z-[100] w-[min(28rem,calc(100vw-1.5rem))] border border-[var(--vmk-border)] bg-[var(--vmk-surface)] p-0 shadow-xl rounded-[10px]"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <Command className="rounded-[10px] bg-[var(--vmk-surface)] [&_[cmdk-group-heading]]:text-[#64748b]">
                      <CommandInput
                        placeholder={t('monteursSearchPlaceholder')}
                        value={monteurSearch}
                        onValueChange={setMonteurSearch}
                        className="h-11 border-b border-[var(--vmk-border)] text-[var(--vmk-text)]"
                      />
                      <CommandList className="max-h-[min(280px,50vh)] overflow-y-auto">
                          <CommandEmpty>
                            <div className="px-3 py-3">
                              <p className="text-xs text-[#64748b] mb-3 leading-snug">
                                {t('monteursNoMatches')}
                              </p>
                              <button
                                type="button"
                                className="vmk-btn vmk-btn-primary w-full justify-center text-sm py-2.5 min-h-0 font-semibold"
                                disabled={!monteurSearch.trim()}
                                onClick={() => {
                                  const name = monteurSearch.trim();
                                  if (!name) return;
                                  setFormData((prev) => ({ ...prev, monteurs: name }));
                                  setMonteursOpen(false);
                                }}
                              >
                                {t('monteursApplyManual')}
                              </button>
                            </div>
                          </CommandEmpty>

                          {monteurSearch.trim() ? (
                            <CommandGroup heading={t('monteursQuickCustom')}>
                              <CommandItem
                                value={`__custom__${monteurSearch}`}
                                onSelect={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    monteurs: monteurSearch.trim(),
                                  }));
                                  setMonteursOpen(false);
                                }}
                                className="cursor-pointer aria-selected:bg-[var(--vmk-primary-light)]"
                              >
                                <span className="font-medium text-[var(--vmk-text)]">
                                  {t('monteursUseTypedName')}
                                </span>
                                <span className="ml-auto truncate text-xs text-[#64748b] max-w-[45%]">
                                  {monteurSearch.trim()}
                                </span>
                              </CommandItem>
                            </CommandGroup>
                          ) : null}

                          <CommandGroup heading={t('monteursRegisteredTeams')}>
                            {sortedTeams.map((team) => (
                              <CommandItem
                                key={team.id}
                                value={`${String(team.name || '')} ${team.id}`}
                                onSelect={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    monteurs: String(team.name || ''),
                                  }));
                                  setMonteursOpen(false);
                                }}
                                className="cursor-pointer aria-selected:bg-[var(--vmk-primary-light)]"
                              >
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10 shadow-sm"
                                  style={{
                                    backgroundColor: team.color || '#94a3b8',
                                  }}
                                  aria-hidden
                                />
                                <span className="truncate font-medium">{team.name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>

                          {formData.monteurs?.trim() ? (
                            <>
                              <CommandSeparator className="bg-[var(--vmk-border)]" />
                              <CommandGroup>
                                <CommandItem
                                  value="__clear_monteurs__"
                                  onSelect={() => {
                                    setFormData((prev) => ({ ...prev, monteurs: '' }));
                                    setMonteursOpen(false);
                                  }}
                                  className="cursor-pointer text-[#64748b] aria-selected:bg-[var(--vmk-border-light)]"
                                >
                                  {t('monteursClearSelection')}
                                </CommandItem>
                              </CommandGroup>
                            </>
                          ) : null}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                )}
              </Popover>
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-[var(--vmk-border-light)] bg-[var(--vmk-background)] px-3 py-2.5 text-xs leading-relaxed">
                {assignedTeam ? (
                  <>
                    <CheckCircle
                      className="shrink-0 text-[var(--vmk-primary)] mt-0.5"
                      size={18}
                      weight="fill"
                      aria-hidden
                    />
                    <span className="text-[var(--vmk-text-secondary)]">{t('monteursLinkedTeam')}</span>
                  </>
                ) : formData.monteurs?.trim() ? (
                  <>
                    <UsersThree
                      className="shrink-0 text-amber-600 mt-0.5"
                      size={18}
                      weight="duotone"
                      aria-hidden
                    />
                    <span className="text-[var(--vmk-text-secondary)]">{t('monteursManualOnly')}</span>
                  </>
                ) : (
                  <span className="text-[var(--vmk-text-muted)]">{t('monteursTeamHint')}</span>
                )}
              </div>
            </div>
          </div>
        </div>

      {isManualOffer ? (
        <div className="form-section">
          <h3 className="form-section-title">{t('manualOrderSection')}</h3>
          <p className="text-sm text-[#64748b] mb-4">{t('manualOrderHint')}</p>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">{t('manualOfferReference')}</label>
              <input
                type="text"
                name="manual_reference"
                value={formData.manual_reference}
                onChange={handleChange}
                className="vmk-input"
                placeholder={t('manualOfferReferencePlaceholder')}
                data-testid="form-manual-reference"
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('amount')}</label>
              <input
                type="number"
                name="offer_total_amount"
                value={formData.offer_total_amount ?? ''}
                onChange={handleChange}
                className="vmk-input"
                min="0"
                step="0.01"
                placeholder="0.00"
                data-testid="form-offer-total-amount"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Client Information */}
      <div className="form-section">
        <h3 className="form-section-title">{t('clientInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('klant')}</label>
            <input
              type="text"
              name="client_name"
              value={formData.client_name}
              onChange={clientFieldsEditable ? handleChange : undefined}
              className="vmk-input"
              readOnly={!clientFieldsEditable}
              data-testid="form-client-name"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('telefoonnummer')}</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={clientFieldsEditable ? handleChange : undefined}
              className="vmk-input"
              readOnly={!clientFieldsEditable}
              data-testid="form-phone"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('adres')}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={clientFieldsEditable ? handleChange : undefined}
              className="vmk-input"
              readOnly={!clientFieldsEditable}
              data-testid="form-address"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('emailLabel')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={clientFieldsEditable ? handleChange : undefined}
              className="vmk-input"
              readOnly={!clientFieldsEditable}
              data-testid="form-email"
            />
          </div>
          <div className="form-group col-span-2">
            <label className="form-label">{t('postcodeWoonplaats')}</label>
            <input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={clientFieldsEditable ? handleChange : undefined}
              className="vmk-input"
              readOnly={!clientFieldsEditable}
              data-testid="form-postcode"
            />
          </div>
        </div>
      </div>

      {/* Color Section */}
      <div className="form-section">
        <h3 className="form-section-title">{t('kleur')}</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="form-group">
            <label className="form-label">{t('kozijnBuitenzijde')}</label>
            <input type="text" name="kozijn_buiten" value={formData.kozijn_buiten} onChange={handleChange} className="vmk-input" data-testid="form-kozijn-buiten" />
            <div className="flex items-center gap-2 mt-3">
              <Checkbox id="folie_kozijn_buiten" checked={formData.folie_kozijn_buiten} onCheckedChange={(checked) => handleCheckboxChange('folie_kozijn_buiten', checked)} data-testid="form-folie-kozijn-buiten" className="border-[#e2e8f0] data-[state=checked]:bg-[#00CBA9] data-[state=checked]:border-[#00CBA9]" />
              <Label htmlFor="folie_kozijn_buiten" className="text-sm text-[#64748b]">{t('folie')}</Label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('draaidealBuitenzijde')}</label>
            <input type="text" name="draaideel_buiten" value={formData.draaideel_buiten} onChange={handleChange} className="vmk-input" data-testid="form-draaideel-buiten" />
            <div className="flex items-center gap-2 mt-3">
              <Checkbox id="folie_draaideel_buiten" checked={formData.folie_draaideel_buiten} onCheckedChange={(checked) => handleCheckboxChange('folie_draaideel_buiten', checked)} data-testid="form-folie-draaideel-buiten" className="border-[#e2e8f0] data-[state=checked]:bg-[#00CBA9] data-[state=checked]:border-[#00CBA9]" />
              <Label htmlFor="folie_draaideel_buiten" className="text-sm text-[#64748b]">{t('folie')}</Label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('kozijnBinnenzijde')}</label>
            <input type="text" name="kozijn_binnen" value={formData.kozijn_binnen} onChange={handleChange} className="vmk-input" data-testid="form-kozijn-binnen" />
            <div className="flex items-center gap-2 mt-3">
              <Checkbox id="folie_kozijn_binnen" checked={formData.folie_kozijn_binnen} onCheckedChange={(checked) => handleCheckboxChange('folie_kozijn_binnen', checked)} data-testid="form-folie-kozijn-binnen" className="border-[#e2e8f0] data-[state=checked]:bg-[#00CBA9] data-[state=checked]:border-[#00CBA9]" />
              <Label htmlFor="folie_kozijn_binnen" className="text-sm text-[#64748b]">{t('folie')}</Label>
            </div>
          </div>
        </div>
      </div>

      {/* Dates Section */}
      <div className="form-section">
        <h3 className="form-section-title">{t('dates')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('verkoopdatum')}</label>
            <input type="date" name="verkoopdatum" value={formData.verkoopdatum} onChange={handleChange} className="vmk-input" data-testid="form-verkoopdatum" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('inmeetdatum')}</label>
            <input type="date" name="inmeetdatum" value={formData.inmeetdatum} onChange={handleChange} className="vmk-input" data-testid="form-inmeetdatum" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('montagedatum')}</label>
            <input type="date" name="montagedatum" value={formData.montagedatum} onChange={handleChange} className="vmk-input" data-testid="form-montagedatum" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('montagedagen')}</label>
            <input type="number" name="montagedagen" value={formData.montagedagen} onChange={handleChange} className="vmk-input" min="0" data-testid="form-montagedagen" />
          </div>
          <div className="form-group col-span-2">
            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="glasbok_required"
                checked={!!formData.glasbok_required}
                onCheckedChange={(checked) => handleCheckboxChange('glasbok_required', !!checked)}
                data-testid="form-glasbok-required"
                className="border-[#e2e8f0] data-[state=checked]:bg-[#00CBA9] data-[state=checked]:border-[#00CBA9]"
              />
              <Label htmlFor="glasbok_required" className="text-sm text-[#334155]">
                {t('glasbokLabel')}
              </Label>
            </div>
          </div>
        </div>
      </div>

      {/* Orders / Bestellingen — each item with Besteldatum + Leverdatum */}
      <div className="form-section">
        <h3 className="form-section-title">{t('bestellingen')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e2e8f0]">
                <th className="text-left text-xs text-[#64748b] uppercase py-2 font-semibold w-[35%]">{t('kozijnen').split(' ')[0] === 'Frames' ? 'Item' : 'Artikel'}</th>
                <th className="text-left text-xs text-[#64748b] uppercase py-2 font-semibold w-[32%]">{t('besteldatum')}</th>
                <th className="text-left text-xs text-[#64748b] uppercase py-2 font-semibold w-[33%]">{t('leverdatum')}</th>
              </tr>
            </thead>
            <tbody>
              {BESTELLING_ITEMS.map(({ key, labelKey }) => {
                const isMisc = key === 'diversen';
                return (
                  <tr key={key} className="border-b border-[#f1f5f9]">
                    <td className="py-2 text-sm font-medium text-[var(--vmk-text)]">{t(labelKey)}</td>
                    <td className="py-2 pr-3" colSpan={isMisc ? 2 : 1}>
                      {isMisc ? (
                        <textarea
                          name={`${key}_besteldatum`}
                          value={formData[`${key}_besteldatum`]}
                          onChange={handleChange}
                          className="vmk-input text-sm min-h-[90px] resize-y"
                          placeholder={t('miscellaneousCombinedPlaceholder')}
                          data-testid={`form-${key}-single-input`}
                        />
                      ) : (
                      <input
                        type="text"
                        name={`${key}_besteldatum`}
                        value={formData[`${key}_besteldatum`]}
                        onChange={handleChange}
                        className="vmk-input text-sm h-9"
                        data-testid={`form-${key}-besteldatum`}
                      />
                      )}
                    </td>
                    {!isMisc && (
                    <td className="py-2">
                      <input
                        type="text"
                        name={`${key}_leverdatum`}
                        value={formData[`${key}_leverdatum`]}
                        onChange={handleChange}
                        className="vmk-input text-sm h-9"
                        data-testid={`form-${key}-leverdatum`}
                      />
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div className="form-section">
        <h3 className="form-section-title">{t('notes')}</h3>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="vmk-input min-h-[120px] resize-none"
          placeholder={t('additionalNotes')}
          data-testid="form-notes"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <button type="button" onClick={onClose} className="vmk-btn" data-testid="form-cancel-btn">
          <X size={18} />
          {t('cancel')}
        </button>
        <button type="submit" disabled={saving} className="vmk-btn vmk-btn-primary" data-testid="form-save-btn">
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : (
            <>
              <FloppyDisk size={18} />
              {isEditMode ? t('saveChanges') : t('saveOrder')}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default WerkvoorbereidingForm;
